/**
 * UX-independent navigation resolver.
 *
 * Applies the visibility policy over the MANIFEST, producing a list both the
 * command palette and (later) the nav header can render. The policy:
 *
 *  - featureFlag off        -> excluded (rollout gate, independent of access)
 *  - !access && !trusted    -> excluded (external users see only what they can access)
 *  - !access && trusted     -> included, disabled, with the authorities they'd need
 *  - access                 -> included, enabled
 *
 * Authorities always determine access; `isInternallyTrusted` only decides
 * whether a no-access item is hidden or shown-disabled.
 */
import {
  MANIFEST,
  ManifestEntry,
  NavGroup,
  requiresFullRefresh,
} from './manifest'
import { Permissions, ManifestKey } from './permissions'
import { forPage, forResource } from './routes'

export interface ResolvedItem {
  key: ManifestKey
  label: string
  path: string
  group: string
  keywords: ReadonlyArray<string>
  fullRefresh: boolean
  hasAccess: boolean
  enabled: boolean
  missing: string[]
}

export interface ResolvedGroup {
  group: string
  items: ResolvedItem[]
}

export interface ResolveNavigationInput {
  permissions: Permissions
  isInternallyTrusted: boolean
  featureFlags?: Record<string, boolean>
}

const FALLBACK_GROUP = 'Other'

const GROUP_ORDER: ReadonlyArray<string> = [
  'Onboarding & Access',
  'Asset Management',
  'Operations',
  'Configuration',
  'Insights',
  FALLBACK_GROUP,
]

function pathFor(key: ManifestKey): string {
  const entry = MANIFEST[key]
  return entry.kind === 'page'
    ? forPage(key as Parameters<typeof forPage>[0])
    : forResource(key as Parameters<typeof forResource>[0])
}

export interface ResolvedNavigation {
  items: ResolvedItem[]
  groups: ResolvedGroup[]
}

export function resolveNavigation({
  permissions,
  isInternallyTrusted,
  featureFlags = {},
}: ResolveNavigationInput): ResolvedNavigation {
  const items: ResolvedItem[] = []

  // MANIFEST declaration order is preserved by Object.keys on the literal.
  ;(Object.keys(MANIFEST) as ManifestKey[]).forEach(key => {
    const entry: ManifestEntry = MANIFEST[key]
    if (!entry.paletteNavigable) return

    // Rollout gate — independent of permissions.
    if (entry.featureFlag && featureFlags[entry.featureFlag] !== true) return

    const hasAccess = permissions.can(key, 'read')

    if (!hasAccess && !isInternallyTrusted) return

    items.push({
      key,
      label: entry.label,
      path: pathFor(key),
      group: (entry.navGroup as NavGroup | undefined) ?? FALLBACK_GROUP,
      keywords: entry.keywords ?? [],
      fullRefresh: requiresFullRefresh(entry),
      hasAccess,
      enabled: hasAccess,
      missing: hasAccess ? [] : permissions.missing(key, 'read'),
    })
  })

  const byGroup = new Map<string, ResolvedItem[]>()
  items.forEach(item => {
    const existing = byGroup.get(item.group)
    if (existing) existing.push(item)
    else byGroup.set(item.group, [item])
  })

  const groups: ResolvedGroup[] = [...byGroup.keys()]
    .sort((a, b) => {
      const ai = GROUP_ORDER.indexOf(a)
      const bi = GROUP_ORDER.indexOf(b)
      return (
        (ai === -1 ? GROUP_ORDER.length : ai) -
        (bi === -1 ? GROUP_ORDER.length : bi)
      )
    })
    .map(group => ({ group, items: byGroup.get(group) as ResolvedItem[] }))

  return { items, groups }
}
